import React, { useEffect, useMemo, useState } from "react";
import { Flex, Button, Text } from "@fluentui/react-northstar";
import { useTranslation } from "react-i18next";

import { PageHeader } from "../../page-header/pageHeader";
import { AudioTestState } from "../../../types/Device.types";
import { ConnectedAudioMicButton } from "../audio-mic-button/ConnectedAudioMicButton";

import { AudioTestActiveProps } from "./AudioTestActive.types";
import { useAudioTestActiveStyles } from "./AudioTestActive.styles";

export const AudioTestActive: React.FC<AudioTestActiveProps> = ({
  testState,
  startAudioTestCall,
  cancelTest,
  isMobile,
}) => {
  const audioTestActiveStyles = useAudioTestActiveStyles();
  const { t } = useTranslation();

  const preCallHeader = t("AUDIO_TEST_PRE_TEST_HEADER");
  const preCallDescription = t("AUDIO_TEST_PRE_TEST_DESCRIPTION");
  const initiatedHeader = t("AUDIO_TEST_INITIATED_HEADER");
  const initiatedDescription = t("AUDIO_TEST_INITIATED_DESCRIPTION");

  const [headerText, setHeaderText] = useState(preCallHeader);
  const [descriptionText, setDescriptionText] = useState(preCallDescription);

  const getTestPrompt = useMemo(() => {
    if (testState === AudioTestState.INITIATED && isMobile) {
      return t("AUDIO_TEST_START_PROMPT_MOBILE");
    } else if (testState === AudioTestState.INITIATED && !isMobile) {
      return t("AUDIO_TEST_START_PROMPT_DESKTOP");
    } else return t("AUDIO_TEST_IN_PROGRESS");
  }, [testState, isMobile, t]);

  useEffect(() => {
    if (testState === AudioTestState.INITIATED) {
      setHeaderText(preCallHeader);
      setDescriptionText(preCallDescription);
    } else {
      setHeaderText(initiatedHeader);
      setDescriptionText(initiatedDescription);
    }
  }, [
    testState,
    preCallHeader,
    preCallDescription,
    initiatedHeader,
    initiatedDescription,
  ]);

  return (
    <Flex
      column
      fill
      styles={audioTestActiveStyles.container}
      data-testid="audioTestContainer"
      padding="padding.medium"
    >
      <Flex.Item size="size.half">
        <Flex hAlign="center">
          <PageHeader
            headerStyles={audioTestActiveStyles.header}
            headerContent={headerText}
            descriptionStyles={audioTestActiveStyles.description}
            descriptionContent={descriptionText}
          />
        </Flex>
      </Flex.Item>

      <Flex.Item size="size.quarter">
        <Flex column hAlign="center" gap="gap.medium">
          <ConnectedAudioMicButton startAudioTestCall={startAudioTestCall} />

          <Text
            aria-hidden="true"
            styles={audioTestActiveStyles.testStartPrompt}
            size="large"
            align="center"
            weight="regular"
            content={<>{getTestPrompt}</>}
          />
        </Flex>
      </Flex.Item>

      <Flex.Item size="size.quarter">
        <Flex column vAlign="center" hAlign="center">
          {isMobile && (
            <Button
              className="focus-button"
              styles={audioTestActiveStyles.cancelButton}
              content={
                <Text
                  content={<>{t("CANCEL_TEST")}</>}
                  weight="semibold"
                  size="medium"
                  align="center"
                />
              }
              data-testid="cancelTestButton"
              onClick={cancelTest}
            />
          )}
        </Flex>
      </Flex.Item>
    </Flex>
  );
};
